import { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { PreSaleBanner } from '../components/layout/PreSaleBanner';
import { Navbar } from '../components/layout/Navbar';
import { Footer } from '../components/layout/Footer';
import { SEO } from '../components/SEO';
import { motion, AnimatePresence } from 'framer-motion';
import { Beaker, Factory, Truck, CreditCard, Megaphone, Server, TrendingUp, Receipt } from 'lucide-react';
import { FlowBackground } from '../components/home/FlowBackground';
import { PAGE_BG, GLASS, PAGE_TITLE } from '../components/home/glass';

// ──────────────────────────────────────────────────────────────────
// COST MODEL
// All figures in EUR, per pouch. The price shown to the customer is
// gross, it includes 9% Dutch VAT (supplements fall under the
// reduced food-supplement rate). So for every €35 we collect, €2.56
// is remitted to the Belastingdienst and €28.44 is net revenue that
// has to cover the cost lines below.
// ──────────────────────────────────────────────────────────────────

const TIERS = {
    preorder: {
        key: 'preorder',
        price: 35,
        priceLabel: '€35',
        unit_en: 'one-time · pre-sale',
        unit_nl: 'eenmalig · voorverkoop',
        items: [
            { key: 'ingredients', value: 11.50, icon: Beaker },
            { key: 'manufacturing', value: 2.00, icon: Factory },
            { key: 'fulfilment', value: 4.00, icon: Truck },
            { key: 'payment', value: 1.15, icon: CreditCard },
            { key: 'acquisition', value: 9.00, icon: Megaphone },
            { key: 'operations', value: 2.50, icon: Server },
            { key: 'vat', value: 2.89, icon: Receipt },
            { key: 'margin', value: 1.96, icon: TrendingUp },
        ],
    },
    oneoff: {
        key: 'oneoff',
        price: 45,
        priceLabel: '€45',
        unit_en: 'one-time · post-launch',
        unit_nl: 'eenmalig · na lancering',
        items: [
            { key: 'ingredients', value: 11.50, icon: Beaker },
            { key: 'manufacturing', value: 2.00, icon: Factory },
            { key: 'fulfilment', value: 4.00, icon: Truck },
            { key: 'payment', value: 1.74, icon: CreditCard },
            { key: 'acquisition', value: 15.00, icon: Megaphone },
            { key: 'operations', value: 2.50, icon: Server },
            { key: 'vat', value: 3.72, icon: Receipt },
            { key: 'margin', value: 4.54, icon: TrendingUp },
        ],
    },
    sub1: {
        key: 'sub1',
        price: 42,
        priceLabel: '€42',
        unit_en: '1-month supply',
        unit_nl: '1 maand voorraad',
        items: [
            { key: 'ingredients', value: 11.50, icon: Beaker },
            { key: 'manufacturing', value: 2.00, icon: Factory },
            { key: 'fulfilment', value: 4.00, icon: Truck },
            { key: 'payment', value: 1.64, icon: CreditCard },
            { key: 'acquisition', value: 11.00, icon: Megaphone },
            { key: 'operations', value: 2.50, icon: Server },
            { key: 'vat', value: 3.47, icon: Receipt },
            { key: 'margin', value: 5.89, icon: TrendingUp },
        ],
    },
    sub3: {
        key: 'sub3',
        price: 39,
        priceLabel: '€39',
        unit_en: '3-month supply',
        unit_nl: '3 maanden voorraad',
        items: [
            { key: 'ingredients', value: 11.50, icon: Beaker },
            { key: 'manufacturing', value: 2.00, icon: Factory },
            { key: 'fulfilment', value: 4.00, icon: Truck },
            { key: 'payment', value: 1.29, icon: CreditCard },
            { key: 'acquisition', value: 6.00, icon: Megaphone },
            { key: 'operations', value: 2.50, icon: Server },
            { key: 'vat', value: 3.22, icon: Receipt },
            { key: 'margin', value: 8.49, icon: TrendingUp },
        ],
    },
    sub6: {
        key: 'sub6',
        price: 35,
        priceLabel: '€35',
        unit_en: '6-month supply',
        unit_nl: '6 maanden voorraad',
        items: [
            { key: 'ingredients', value: 11.50, icon: Beaker },
            { key: 'manufacturing', value: 2.00, icon: Factory },
            { key: 'fulfilment', value: 4.00, icon: Truck },
            { key: 'payment', value: 1.15, icon: CreditCard },
            { key: 'acquisition', value: 4.50, icon: Megaphone },
            { key: 'operations', value: 2.50, icon: Server },
            { key: 'vat', value: 2.89, icon: Receipt },
            { key: 'margin', value: 6.46, icon: TrendingUp },
        ],
    },
};

const CATEGORY_COPY = {
    ingredients: {
        color: '#0047AB',
        label_en: 'Ingredients & pouch',
        label_nl: 'Ingrediënten & zakje',
        detail_en: 'Five functional ingredients at clinical-study doses, plus the pouch itself, labelling, and the scoop.',
        detail_nl: 'Vijf functionele ingrediënten in klinische doses, plus het zakje, etiketten en de maatschep.',
    },
    manufacturing: {
        color: '#1E6FCC',
        label_en: 'Manufacturing & QC',
        label_nl: 'Productie & kwaliteitscontrole',
        detail_en: 'EU GMP-certified blending, filling, sealing, and third-party batch testing.',
        detail_nl: 'EU GMP-gecertificeerd mengen, vullen, sluiten en batchtesten door derden.',
    },
    fulfilment: {
        color: '#4B93DB',
        label_en: 'Fulfilment & shipping',
        label_nl: 'Fulfilment & verzending',
        detail_en: 'Pick, pack, outer packaging, and PostNL shipping within the Netherlands.',
        detail_nl: 'Picken, verpakken, buitenverpakking en PostNL-verzending binnen Nederland.',
    },
    payment: {
        color: '#7FB0E5',
        label_en: 'Payment processing',
        label_nl: 'Betaalverwerking',
        detail_en: 'Stripe and card network fees (~2.9% + €0.25 per transaction).',
        detail_nl: 'Stripe- en kaartnetwerk­kosten (~2,9% + €0,25 per transactie).',
    },
    acquisition: {
        color: '#FBCFE8',
        iconColor: '#BE185D',
        label_en: 'Customer acquisition',
        label_nl: 'Klantacquisitie',
        detail_en: 'Ads, content, and outreach. This is the line that falls when you subscribe for longer.',
        detail_nl: 'Advertenties, content en outreach. Dit is de post die daalt naarmate je langer bij ons blijft.',
    },
    operations: {
        color: '#CBD5E1',
        iconColor: '#334155',
        label_en: 'Operations',
        label_nl: 'Operations',
        detail_en: 'Website hosting, email service, customer support, accounting, admin.',
        detail_nl: 'Websitehosting, e-maildienst, klantenservice, boekhouding, administratie.',
    },
    vat: {
        color: '#FDE68A',
        iconColor: '#B45309',
        label_en: 'VAT (9%)',
        label_nl: 'BTW (9%)',
        detail_en: 'The 9% Dutch reduced-rate VAT included in the price. Remitted to the Belastingdienst, it never belongs to us.',
        detail_nl: 'De 9% Nederlandse lage BTW die in de prijs is inbegrepen. We dragen dit af aan de Belastingdienst, het is nooit van ons.',
    },
    margin: {
        color: '#BBF7D0',
        iconColor: '#047857',
        label_en: 'Net margin (reinvested)',
        label_nl: 'Nettomarge (herinvesteerd)',
        detail_en: 'What\'s left. Reinvested in inventory, clinical research, and new formulations. No investors to pay dividends to, yet.',
        detail_nl: 'Wat overblijft. Geïnvesteerd in voorraad, klinisch onderzoek en nieuwe formuleringen. Nog geen investeerders om dividend aan te betalen.',
    },
};

function Bar({ tier, lang }) {
    return (
        <div className="mb-6">
            <div className="flex rounded-xl overflow-hidden h-10 bg-slate-100 shadow-sm">
                {tier.items.map((item, i) => {
                    const pct = (item.value / tier.price) * 100;
                    const cat = CATEGORY_COPY[item.key];
                    return (
                        <motion.div
                            key={item.key}
                            initial={{ width: 0 }}
                            animate={{ width: `${pct}%` }}
                            transition={{ duration: 0.5, delay: 0.05 * i }}
                            style={{ background: cat.color }}
                            title={`${cat[`label_${lang}`]}, €${item.value.toFixed(2)} (${pct.toFixed(1)}%)`}
                            className="h-full"
                        />
                    );
                })}
            </div>
            <div className="flex justify-between mt-2 text-xs text-muted-foreground">
                <span>€0</span>
                <span>€{tier.price}</span>
            </div>
        </div>
    );
}

function Row({ item, tier, lang }) {
    const cat = CATEGORY_COPY[item.key];
    const pct = (item.value / tier.price) * 100;
    return (
        <motion.div
            layout
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.3 }}
            className="px-4 py-4 last:pb-1 border-b border-border last:border-b-0"
            data-testid={`business-row-${item.key}`}
        >
            {/* Top: icon + title; amount sits left-aligned under the title,
                percentage stays right-aligned on the same row as the amount. */}
            <div className="flex items-start gap-4">
                <div
                    className="w-11 h-11 rounded-lg flex items-center justify-center shrink-0"
                    style={{
                        backgroundColor: `${cat.color}66`,
                        color: cat.iconColor || cat.color,
                    }}
                >
                    <item.icon size={20} strokeWidth={2} />
                </div>
                <div className="flex-1 min-w-0">
                    <h3 className="font-sans text-base font-semibold text-foreground tracking-tight">
                        {cat[`label_${lang}`]}
                    </h3>
                    <div className="flex items-baseline justify-between gap-3 mt-1">
                        <span className="font-sans text-base font-bold text-foreground tabular-nums whitespace-nowrap">
                            €{item.value.toFixed(2)}
                        </span>
                        <span className="text-xs text-muted-foreground tabular-nums whitespace-nowrap">
                            {pct.toFixed(1)}%
                        </span>
                    </div>
                </div>
            </div>
            {/* Body: spans the full row width starting from the icon's left edge. */}
            <p className="text-sm text-slate-600 leading-relaxed mt-3">
                {cat[`detail_${lang}`]}
            </p>
        </motion.div>
    );
}

export default function Business() {
    const { lang } = useLanguage();
    const [tierKey, setTierKey] = useState('preorder');
    const tier = TIERS[tierKey];
    const ingredientRatio = (TIERS.preorder.items[0].value / TIERS.preorder.price * 100).toFixed(0);

    return (
        <div>
            <SEO
                title="Economics & Transparency | Sleepstack"
                description="Full breakdown of how a Sleepstack pouch is priced, ingredients, manufacturing, VAT, margin, and acquisition cost. Built openly."
                path="/business"
            />
            <PreSaleBanner />
            <Navbar />
            <main className="relative isolate glass-scope" style={{ paddingTop: 'var(--page-offset, 128px)', background: PAGE_BG }}>
                <FlowBackground variant="business" />
                {/* Hero */}
                <section className="section-padding">
                    <div className="section-container">
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5 }}
                            className="max-w-3xl"
                        >
                            <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-bold text-primary mb-4">
                                <span className="w-8 h-px bg-primary" />
                                {lang === 'nl' ? 'De business' : 'The business'}
                            </span>
                            <div className={`p-6 sm:p-8 lg:p-10 space-y-5 ${GLASS}`} data-testid="business-hero-panel">
                                <h1 className={PAGE_TITLE} data-testid="business-title">
                                    {lang === 'nl' ? (
                                        <>Waar je geld <span className="text-gradient-blue">naartoe gaat.</span></>
                                    ) : (
                                        <>Where your <span className="text-gradient-blue">money goes.</span></>
                                    )}
                                </h1>
                        <motion.p
                            initial={{ opacity: 0, y: 16 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.45 }}
                            className="text-base md:text-lg text-slate-800 leading-relaxed"
                        >
                            {lang === 'nl'
                                ? 'We zijn transparant over wat er in Sleepstack zit. Dan moeten we ook transparant zijn over hoe de prijs is opgebouwd. Dus hier is elke euro, uitgesplitst.'
                                : 'If we are transparent about the formula, we should also be transparent about the price. So here is every euro, broken down.'}
                        </motion.p>
                        <motion.p
                            initial={{ opacity: 0, y: 16 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.45, delay: 0.05 }}
                            className="text-sm md:text-base text-muted-foreground leading-relaxed"
                        >
                            {lang === 'nl'
                                ? 'De meeste supplementenmerken verbergen deze cijfers met een goede reden: hun ingrediëntenkosten zijn een klein deel van wat jij betaalt. Bij ons is het het grootste deel. Dat verandert niet door er over te zwijgen.'
                                : 'Most supplement brands hide these numbers for a good reason: their ingredient cost is a small share of what you pay. Here, it\'s the biggest one. Staying quiet about it wouldn\'t change that.'}
                        </motion.p>
                            </div>
                        </motion.div>
                    </div>
                </section>

                {/* Interactive breakdown */}
                <section className="bg-accent py-16 md:py-20" id="breakdown">
                    <div className="section-container max-w-4xl">
                        <h2 className="heading-lg text-foreground mb-3">
                            {lang === 'nl' ? 'Kies je prijspunt.' : 'Pick your price point.'}
                        </h2>
                        <p className="text-sm md:text-base text-muted-foreground mb-10 max-w-2xl leading-relaxed">
                            {lang === 'nl'
                                ? 'Dezelfde formule, dezelfde productiekosten, verschillende distributiekosten. Schakel hieronder om te zien hoe jouw keuze elke euro herverdeelt.'
                                : 'Same formula, same production cost, different distribution costs. Switch below to see how your choice re-distributes each euro.'}
                        </p>

                        {/* Tier selector, 2x2 grid on narrow screens (equal column widths
                            regardless of label length), 4 across from sm and up. */}
                        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 auto-rows-fr gap-2 mb-8">
                            {Object.values(TIERS).map(t => (
                                <button
                                    key={t.key}
                                    onClick={() => setTierKey(t.key)}
                                    data-testid={`tier-btn-${t.key}`}
                                    className={`text-left px-5 py-3 rounded-xl border-2 transition-all ${
                                        tierKey === t.key
                                            ? 'bg-primary border-primary text-white'
                                            : 'bg-white border-border text-foreground hover:border-primary/50'
                                    }`}
                                >
                                    <div className="font-condensed text-2xl font-bold leading-none">
                                        {t.priceLabel}
                                    </div>
                                    <div className={`text-xs mt-1 ${tierKey === t.key ? 'text-white/80' : 'text-muted-foreground'}`}>
                                        {t[`unit_${lang}`]}
                                    </div>
                                </button>
                            ))}
                        </div>

                        {/* Breakdown card */}
                        <AnimatePresence mode="wait">
                            <motion.div
                                key={tier.key}
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -10 }}
                                transition={{ duration: 0.25 }}
                                className="bg-white rounded-2xl border border-border px-6 pt-6 pb-4 md:px-8 md:pt-8 md:pb-4"
                            >
                                {/* Header */}
                                <div className="flex items-end justify-between gap-3 mb-6">
                                    <div className="min-w-0">
                                        <div className="text-[10px] sm:text-xs uppercase tracking-widest font-bold text-muted-foreground">
                                            {lang === 'nl' ? 'Je betaalt' : 'You pay'}
                                        </div>
                                        <div className="flex items-baseline gap-2 sm:gap-3 mt-1 flex-wrap">
                                            <span className="font-condensed text-4xl sm:text-5xl md:text-6xl font-bold text-foreground leading-none">
                                                {tier.priceLabel}
                                            </span>
                                            <span className="text-xs sm:text-sm text-muted-foreground">
                                                {tier[`unit_${lang}`]}
                                            </span>
                                        </div>
                                    </div>
                                    <div className="text-right shrink-0">
                                        <div className="text-[10px] sm:text-xs uppercase tracking-widest font-bold text-muted-foreground">
                                            {lang === 'nl' ? 'Nettomarge' : 'Net margin'}
                                        </div>
                                        <div className="font-condensed text-2xl sm:text-3xl font-bold text-primary leading-none mt-1">
                                            €{tier.items.find(i => i.key === 'margin').value.toFixed(2)}
                                        </div>
                                    </div>
                                </div>

                                <Bar tier={tier} lang={lang} />

                                <div>
                                    {tier.items.map(item => (
                                        <Row key={item.key} item={item} tier={tier} lang={lang} />
                                    ))}
                                </div>
                            </motion.div>
                        </AnimatePresence>
                    </div>
                </section>

                {/* Why ingredient cost is high */}
                <section className="bg-white py-16 md:py-20">
                    <div className="section-container max-w-3xl">
                        <h2 className="heading-lg text-foreground mb-4">
                            {lang === 'nl' ? 'Waarom onze ingrediënten meer kosten.' : 'Why our ingredients cost more.'}
                        </h2>
                        <p className="text-sm md:text-base text-muted-foreground leading-relaxed mb-10">
                            {lang === 'nl'
                                ? 'De meeste supplementenmerken besteden tussen de 5% en 15% van de verkoopprijs aan ingrediënten, de goedkoopste massaproducten zitten aan de onderkant, premium DTC-merken aan de bovenkant. Sleepstack besteedt ongeveer '
                                : 'Most supplement brands spend somewhere between 5% and 15% of the retail price on ingredients, cheap mass-market products sit at the bottom of that range, premium DTC brands at the top. Sleepstack spends about '}
                            <strong className="text-foreground">{ingredientRatio}%</strong>
                            {lang === 'nl' ? '. Waarom?' : '. Why?'}
                        </p>

                        <div className="space-y-5">
                            {[
                                {
                                    title_en: 'Chelated minerals',
                                    title_nl: 'Gechelateerde mineralen',
                                    body_en: 'Magnesium bisglycinate (magnesium bound to glycine) costs around 5× more per milligram of elemental magnesium than magnesium oxide, the cheap form most brands use. It\'s worth it: bisglycinate is better absorbed and gentler on the stomach.',
                                    body_nl: 'Magnesium bisglycinaat (magnesium gebonden aan glycine) kost ongeveer 5× zoveel per milligram elementair magnesium als magnesiumoxide, de goedkope vorm die de meeste merken gebruiken. Het is het waard: bisglycinaat wordt beter opgenomen en is zachter voor de maag.',
                                },
                                {
                                    title_en: 'Clinical doses, not sprinkle doses',
                                    title_nl: 'Klinische doses, geen strooidoses',
                                    body_en: 'A typical "sleep complex" might contain 500mg of glycine. We use 3,000mg, six times more, because that\'s the dose every published trial used. Six times the material means six times the cost on that line.',
                                    body_nl: 'Een typische "slaapformule" bevat misschien 500mg glycine. Wij gebruiken 3.000mg, zes keer zoveel, omdat dat de dosis is die in elke gepubliceerde studie werd gebruikt. Zes keer zoveel materiaal betekent zes keer zoveel kosten op die regel.',
                                },
                                {
                                    title_en: 'Real flavouring, not synthetic',
                                    title_nl: 'Natuurlijke smaakstof, geen synthetische',
                                    body_en: 'Natural cherry flavouring, citric acid for taste balance, and a touch of beetroot extract for natural colouring. No sucralose, no artificial sweeteners, no cheap bulking agents.',
                                    body_nl: 'Natuurlijk kersenaroma, citroenzuur voor smaakbalans en een vleugje bietenrood-extract voor natuurlijke kleuring. Geen sucralose, geen kunstmatige zoetstoffen, geen goedkope bulkmiddelen.',
                                },
                            ].map((r, i) => (
                                <motion.div
                                    key={i}
                                    initial={{ opacity: 0, y: 12 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ duration: 0.4, delay: i * 0.05 }}
                                    className="border-l-4 border-primary pl-5 py-1"
                                >
                                    <h3 className="font-sans text-lg font-bold text-foreground mb-2 tracking-tight">
                                        {lang === 'nl' ? r.title_nl : r.title_en}
                                    </h3>
                                    <p className="text-sm text-slate-700 leading-relaxed">
                                        {lang === 'nl' ? r.body_nl : r.body_en}
                                    </p>
                                </motion.div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* Industry comparison */}
                <section className="bg-white py-16 md:py-20">
                    <div className="section-container max-w-3xl">
                        <h2 className="heading-lg text-foreground mb-8">
                            {lang === 'nl' ? 'De industrievergelijking.' : 'The industry comparison.'}
                        </h2>

                        <div className="space-y-6">
                            <div>
                                <div className="flex items-baseline justify-between mb-2">
                                    <span className="text-sm font-semibold text-muted-foreground">
                                        {lang === 'nl' ? 'Industrie-bandbreedte' : 'Industry range'}
                                    </span>
                                    <span className="font-condensed text-3xl font-bold text-foreground">5–15%</span>
                                </div>
                                <div className="relative h-3 bg-slate-100 rounded-full overflow-hidden">
                                    <motion.div
                                        initial={{ width: 0 }}
                                        whileInView={{ width: '15%' }}
                                        viewport={{ once: true }}
                                        transition={{ duration: 0.9 }}
                                        className="absolute left-0 top-0 h-full bg-slate-300"
                                    />
                                    <motion.div
                                        initial={{ width: 0 }}
                                        whileInView={{ width: '5%' }}
                                        viewport={{ once: true }}
                                        transition={{ duration: 0.7, delay: 0.1 }}
                                        className="absolute left-0 top-0 h-full bg-slate-400"
                                    />
                                </div>
                                <p className="text-xs text-muted-foreground mt-2 leading-relaxed">
                                    {lang === 'nl'
                                        ? 'Aandeel van de verkoopprijs dat naar de daadwerkelijke ingrediënten gaat. Goedkope massaproducten zitten rond de 5%, premium DTC-merken rond de 15%.'
                                        : 'Share of the retail price that goes to actual ingredients. Cheap mass-market products sit around 5%, premium DTC brands around 15%.'}
                                </p>
                            </div>

                            <div>
                                <div className="flex items-baseline justify-between mb-2">
                                    <span className="text-sm font-semibold text-primary">Sleepstack</span>
                                    <span className="font-condensed text-3xl font-bold text-primary">~{ingredientRatio}%</span>
                                </div>
                                <div className="h-3 bg-slate-100 rounded-full overflow-hidden">
                                    <motion.div
                                        initial={{ width: 0 }}
                                        whileInView={{ width: `${ingredientRatio}%` }}
                                        viewport={{ once: true }}
                                        transition={{ duration: 1 }}
                                        className="h-full bg-primary"
                                    />
                                </div>
                                <p className="text-xs text-muted-foreground mt-2 leading-relaxed">
                                    {lang === 'nl'
                                        ? 'Bij €35 (voorverkoop): ruim twee keer de bovenkant van de industriebandbreedte gaat naar de daadwerkelijke formule.'
                                        : 'At €35 (pre-sale): more than twice the top of the industry range goes to the actual formula.'}
                                </p>
                            </div>
                        </div>

                        <div className="mt-10 p-6 bg-accent rounded-2xl border-l-4 border-primary">
                            <p className="text-sm md:text-base text-slate-800 leading-relaxed">
                                {lang === 'nl' ? (
                                    <>
                                        <strong>Kortom:</strong> je geld gaat voornamelijk naar het product, niet naar een reclamebudget.
                                        We verdienen minder per zakje dan het merk op het schap naast ons. Dat is een bewuste keuze,
                                        en we denken dat het de keuze is die het merk de moeite waard maakt om voor te gaan.
                                    </>
                                ) : (
                                    <>
                                        <strong>In short:</strong> your money mostly pays for the product, not the ad budget.
                                        We earn less per pouch than the brand on the shelf next to us. That&apos;s a deliberate choice,
                                        and we think it&apos;s the choice that makes the brand worth being here.
                                    </>
                                )}
                            </p>
                        </div>
                    </div>
                </section>

                {/* Closing */}
                <section className="bg-accent py-16 md:py-20 border-t border-border">
                    <div className="section-container max-w-2xl text-center">
                        <p className="text-sm text-muted-foreground leading-relaxed">
                            {lang === 'nl'
                                ? 'Cijfers zijn actuele benaderingen gebaseerd op ons huidige productievolume. Marges schommelen; we actualiseren deze pagina wanneer de onderliggende kosten veranderen.'
                                : 'Numbers are current approximations based on our current production volume. Margins fluctuate; we update this page when underlying costs change.'}
                        </p>
                    </div>
                </section>
            </main>
            <Footer />
        </div>
    );
}
